import React, { useState, useRef, useEffect } from "react";
import { Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BasicDrawer, BasicModal } from "../../../components/Shared";
import { useCompany, useSite } from "../../../contexts";
import { useAuth } from "../../../hooks";
import { Inspection, Site } from "../../../api";
import { ChecklistInspectionsList }  from "../../../components/Admin/Checklist/ChecklistInspectionsList/ChecklistInspectionsList";
// import { ChecklistTemplatesList } from "../../../components/Admin/Checklist/ChecklistTemplatesList/ChecklistTemplatesList"; // tab comentado por ahora
import { ChecklistForm }             from "../../../components/Admin/Checklist/ChecklistForm/ChecklistForm";
import { ChecklistExecution }        from "../../../components/Admin/Checklist/ChecklistExecution/ChecklistExecution";
import { ChecklistTemplateForm }     from "../../../components/Admin/Checklist/ChecklistTemplateForm/ChecklistTemplateForm";
import { ChecklistDashboard }        from "../../../components/Admin/Checklist/ChecklistDashboard/ChecklistDashboard";
import { ChecklistHome }             from "../../../components/Admin/Checklist/ChecklistHome/ChecklistHome";
import { ChecklistExplore }          from "../../../components/Admin/Checklist/ChecklistExplore/ChecklistExplore";
import { ChecklistCatalogs }         from "../../../components/Admin/Checklist/ChecklistCatalogs/ChecklistCatalogs";
import { NORM_COUNTRIES }            from "../../../components/Admin/Checklist/normCountries";
import { useChecklistSiteIndustry }  from "../../../components/Admin/Checklist/useChecklistSiteIndustry";
import { useCompanyName }            from "../../../components/Admin/Checklist/useCompanyName";
import "./Checklist.scss";

const inspectionCtrl = new Inspection();
const siteCtrl = new Site();

const CURRENT_YEAR = new Date().getFullYear();
// const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - 2 + i).reverse();

const TABS = [
  { key: "inicio",       label: "Inicio"              },
  { key: "dashboard",    label: "Dashboard"           },
  { key: "explorar",     label: "Explorar checklists" },
  { key: "ejecutar",     label: "Ejecutar"            },
  { key: "inspecciones", label: "Registros"           },
  // { key: "plantillas", label: "Plantillas"          }, // comentado por ahora
  { key: "catalogos",    label: "Catálogos"           },
];

export function Checklist() {
  const { company } = useCompany();
  const { site: activeSite } = useSite();
  const { accessToken, user } = useAuth();
  const hasCompany = company && company !== "0" && company !== 0;

  // Pill Empresa · Industria · Sitio — mismo contenido que antes tenía
  // ChecklistHome, ahora en la fila de tabs para que se vea en todo el
  // módulo (no solo en Inicio).
  const companyName = useCompanyName();
  const [siteIndustry] = useChecklistSiteIndustry(activeSite);
  const [siteName, setSiteName] = useState("");

  useEffect(() => {
    if (!activeSite || activeSite === "0") { setSiteName(""); return; }
    siteCtrl.getSite(accessToken, activeSite)
      .then((res) => setSiteName(res?.name || ""))
      .catch(() => setSiteName(""));
  }, [accessToken, activeSite]);

  const industryPillText = [companyName, siteIndustry?.name, siteName].filter(Boolean).join(" · ");

  const [activeTab,     setActiveTab]     = useState("inicio");
  const [showForm,      setShowForm]      = useState(false);
  const [currentInsp,   setCurrentInsp]   = useState(null);
  const [currentTpl,    setCurrentTpl]    = useState(null);
  const [reload,        setReload]        = useState(false);
  const [year] = useState(CURRENT_YEAR); // selector de versión oculto: siempre año actual
  const [showTplForm,   setShowTplForm]   = useState(false);
  const [editTpl,       setEditTpl]       = useState(null);
  const [newTplMode,    setNewTplMode]    = useState("manual");
  // const [reloadTpl,  setReloadTpl]     = useState(false); // solo usado por el tab Plantillas, comentado por ahora
  const [country,       setCountry]       = useState(NORM_COUNTRIES[0].value);
  const startingRef = useRef(false);

  const openForm  = () => setShowForm(true);
  const closeForm = () => setShowForm(false);

  const handleStart = () => {
    // La inspección ya quedó creada (en borrador). Los ítems los completa
    // el inspector desde su celular vía el QR — el supervisor no entra acá
    // a la pantalla de respuestas.
    closeForm();
    setReload((p) => !p);
    toast.success("Inspección creada. Compartí el QR con el inspector para que la complete.");
  };

  // Arranca una inspección para mí mismo ya (desde "Nueva inspección" en
  // Inicio, "más usados" o Explorar): autoasigna inspector = usuario actual
  // y sitio = sitio activo, y pasa directo al tab Ejecutar. Para asignarla a
  // otra persona vía QR sigue estando el wizard completo (+ Nueva inspección
  // desde el tab Inspecciones).
  const startInspection = async (tpl) => {
    if (startingRef.current) return;
    if (!activeSite || activeSite === "0") {
      toast.error("Seleccioná un sitio desde el encabezado antes de empezar.");
      return;
    }
    startingRef.current = true;
    try {
      const payload = {
        template:  tpl._id,
        inspector: user?._id,
        site:      activeSite,
        date:      new Date().toISOString(),
        status:    "draft",
      };
      const res = await inspectionCtrl.createInspection(accessToken, payload);
      setCurrentInsp(res.inspection || res);
      setCurrentTpl(tpl);
      setActiveTab("ejecutar");
    } catch (err) {
      toast.error(err?.msg || "Error al iniciar la inspección.");
    } finally {
      startingRef.current = false;
    }
  };

  const handleComplete = () => {
    setCurrentInsp(null);
    setCurrentTpl(null);
    setReload((p) => !p);
    setActiveTab("inspecciones");
    toast.success("Inspección completada y guardada.");
  };

  const handleEdit = ({ inspection, template }) => {
    setCurrentInsp(inspection);
    setCurrentTpl(template);
    setActiveTab("ejecutar");
  };

  const closeExecution = () => {
    setCurrentInsp(null);
    setCurrentTpl(null);
    setReload((p) => !p);
    setActiveTab("inspecciones");
  };

  // El botón "Crear checklist propio" ahora elige el modo de derivación
  // (manual/auto) antes de abrir el modal — ver NewChecklistButton. En
  // Figma son dos modales, pero comparten los mismos campos salvo ese
  // bloque, así que se resuelve con un solo componente + modo fijo.
  const openNewTemplate  = (mode = "manual") => {
    setEditTpl(null);
    setNewTplMode(mode);
    setShowTplForm(true);
  };
  // const openEditTemplate = (tpl) => { setEditTpl(tpl); setShowTplForm(true); }; // solo usado por el tab Plantillas, comentado por ahora
  const closeTplForm     = () => { setShowTplForm(false); setEditTpl(null); };
  const handleTplSaved   = () => {
    closeTplForm();
    toast.success(editTpl ? "Plantilla actualizada." : "Plantilla creada.");
  };

  return (
    <div className="chl-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="chl-page__banner">
        <div className="chl-page__banner-left">
          <span className="chl-page__banner-title">Checklist de Inspecciones</span>
        </div>
        {/* Selector de versión (año) oculto en el header
        {(activeTab === "inspecciones") && (
          <div className="chl-page__banner-controls">
            <div className="chl-page__year-pill">
              <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12" className="chl-page__year-pill__icon">
                <path d="M5 3a1 1 0 0 0-1 1v1H3a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1V4a1 1 0 0 0-1-1H5zm1 2V4h4v1H6zM3 7h10v5H3V7z"/>
              </svg>
              <span className="chl-page__year-pill__label">Versión {year}</span>
              <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9" className="chl-page__year-pill__arrow">
                <path d="M6 8L2 4h8z"/>
              </svg>
              <select
                className="chl-page__year-pill__native"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        )}
        */}
      </div>

      {/* Tabs + pill Empresa · Industria · Sitio */}
      <div className="chl-page__tabs-row">
        <div className="chl-page__tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`chl-page__tab${activeTab === t.key ? " chl-page__tab--active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        {industryPillText && (
          <span className="chl-page__industry-pill">{industryPillText}</span>
        )}
      </div>

      <div className="chl-page__body">
        {hasCompany ? (
          <>
            {activeTab === "inicio" && (
              <ChecklistHome
                onNewInspection={() => setActiveTab("explorar")}
                onGoRecords={() => setActiveTab("inspecciones")}
                onGoTemplates={() => setActiveTab("explorar")}
                onUseTemplate={startInspection}
              />
            )}
            {activeTab === "explorar" && (
              <ChecklistExplore
                onSelectTemplate={startInspection}
                onCreateTemplate={openNewTemplate}
                country={country}
                onCountryChange={setCountry}
              />
            )}
            {activeTab === "dashboard" && (
              <ChecklistDashboard year={year} reload={reload} />
            )}
            {activeTab === "ejecutar" && (
              currentInsp && currentTpl ? (
                <ChecklistExecution
                  inspection={currentInsp}
                  template={currentTpl}
                  close={closeExecution}
                  onComplete={handleComplete}
                  country={country}
                  onCountryChange={setCountry}
                />
              ) : (
                <div className="chl-page__empty-exec">
                  <Icon name="clipboard outline" size="huge" />
                  <p>Elegí un checklist desde "Explorar checklists" para empezar una inspección.</p>
                  <button type="button" className="chl-page__empty-exec-btn" onClick={() => setActiveTab("explorar")}>
                    Ir a Explorar checklists
                  </button>
                </div>
              )
            )}
            {activeTab === "inspecciones" && (
              <ChecklistInspectionsList reload={reload} onNew={openForm} onEdit={handleEdit} year={year} />
            )}
            {/* {activeTab === "plantillas" && (
              <ChecklistTemplatesList reload={reloadTpl} onNew={openNewTemplate} onEdit={openEditTemplate} />
            )} */}
            {activeTab === "catalogos" && (
              <ChecklistCatalogs
                onCreateTemplate={openNewTemplate}
                onGoTemplates={() => setActiveTab("explorar")}
              />
            )}
          </>
        ) : (
          <div className="no-site-warning">
            <Icon name="building outline" />
            <p>Seleccioná una empresa desde el encabezado</p>
          </div>
        )}
      </div>

      {/* Drawer: nueva inspección (wizard completo, para asignarla a otra persona vía QR) */}
      <BasicDrawer show={showForm} close={closeForm} width="80vw" hideHeader flush>
        {showForm && (
          <ChecklistForm close={closeForm} onStart={handleStart} />
        )}
      </BasicDrawer>

      {/* Modal: crear/editar plantilla */}
      <BasicModal show={showTplForm} close={closeTplForm} className="tpl-modal">
        {showTplForm && (
          <ChecklistTemplateForm
            template={editTpl}
            initialDerivationMode={newTplMode}
            country={country}
            close={closeTplForm}
            onSaved={handleTplSaved}
          />
        )}
      </BasicModal>
    </div>
  );
}
